import React from "react";
import SectionHeader from "../components/SectionHeader";
import { Award, Globe2, Building2, ShieldCheck, Satellite, Sprout, Radio, Cpu } from "lucide-react";

const WORKER_IMG = "https://images.pexels.com/photos/8487792/pexels-photo-8487792.jpeg";
const FIELD_IMG = "https://images.pexels.com/photos/2132180/pexels-photo-2132180.jpeg";

const CAPABILITY_TAGS = [
  { icon: Sprout, label: "Precision Irrigation DSS" },
  { icon: Satellite, label: "Satellite Analytics" },
  { icon: Cpu, label: "AgriPad Smart Field Devices" },
  { icon: Radio, label: "RF over Fiber" },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pb-24">
      <section className="relative border-b border-slate-800 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${WORKER_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 to-slate-950/70" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="font-mono-spec text-xs text-amber-400 mb-4">ABOUT OLIVINE</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-3xl">
            Intelligent, integrated innovation for <span className="text-amber-400">real-world industry.</span>
          </h1>
          <p className="mt-6 text-slate-300 max-w-3xl text-lg leading-relaxed">
            Olivine Global Systems is a technology-driven solutions provider focused on transforming agriculture, industry and rural ecosystems through intelligent, integrated digital innovations.
          </p>
        </div>
      </section>

      {/* COMPANY STORY */}
      <section className="py-20 border-b border-slate-800" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-3">
            <SectionHeader kicker="OUR STORY" title="From farm to factory — engineered intelligence." testIdPrefix="story" />
            <div className="mt-8 space-y-5 text-slate-300 leading-relaxed">
              <p>
                Olivine Global Systems is a technology-driven solutions provider focused on transforming agriculture and rural ecosystems through intelligent, integrated digital innovations. The company specializes in developing advanced platforms such as <span className="text-amber-400 font-semibold">precision irrigation decision support systems</span>, <span className="text-amber-400 font-semibold">AI-enabled fire alert and prevention solutions</span>, and smart field devices like <span className="text-amber-400 font-semibold">AgriPad</span> that empower farmers with real-time, data-driven insights.
              </p>
              <p>
                By combining <span className="text-white font-semibold">IoT, satellite analytics, AI, and communication technologies such as RF over Fiber</span>, Olivine delivers scalable and reliable solutions tailored for rural and remote environments.
              </p>
              <p>
                With a strong commitment to sustainability, efficiency, and inclusive growth, the company aims to enhance productivity, optimize resource utilization, and enable smarter decision-making across the industry value chain.
              </p>
            </div>

            {/* capability tags */}
            <div className="mt-10 flex flex-wrap gap-3" data-testid="about-capability-tags">
              {CAPABILITY_TAGS.map((c) => (
                <div key={c.label} className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500/30 bg-slate-950 text-amber-300 font-mono-spec text-xs">
                  <c.icon size={13} /> {c.label}
                </div>
              ))}
            </div>
          </div>

          {/* image panel */}
          <div className="lg:col-span-2">
            <div className="relative border border-slate-800 overflow-hidden aspect-[4/5] bg-slate-900">
              <img src={FIELD_IMG} alt="Precision agriculture" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="font-mono-spec text-[10px] text-amber-400">FIELD + FACTORY</div>
                <div className="mt-2 font-display font-bold text-white text-lg leading-tight">One integrated stack — IoT, AI, satellite and RF — engineered for the harshest environments.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHeader kicker="OUR MISSION" title="Eliminate preventable industrial failure. Enable smarter rural growth." testIdPrefix="mission" />
            <p className="mt-6 text-slate-400 leading-relaxed">
              Whether a 33kV panel in a steel plant, an irrigation valve in a remote farm, or a cable run across a forest, the principle is the same — sense earlier, act smarter, protect more. Our mandate is to break that failure chain end to end: sensing, intervention, education and protection.
            </p>
            <p className="mt-4 text-slate-400 leading-relaxed">
              We combine POWER-ON electrical cleaning, IoT-based predictive monitoring, AI-generated safety training, satellite analytics and fire-protection chemistry into a coherent stack — so our customers never have to choose between uptime, safety and sustainability.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-0 border border-slate-800 grid-border">
            {[
              { icon: Award, num: "20+", label: "Years of field research" },
              { icon: ShieldCheck, num: "0", label: "Safety incidents" },
              { icon: Globe2, num: "3", label: "Continents of deployment" },
              { icon: Building2, num: "50kV", label: "Max voltage live-load" },
            ].map((x, i) => (
              <div key={i} className="p-8 bg-slate-950" data-testid={`about-stat-${i}`}>
                <x.icon className="text-amber-400" size={22} />
                <div className="mt-4 font-display font-extrabold text-4xl text-white">{x.num}</div>
                <div className="mt-2 font-mono-spec text-xs text-slate-400">{x.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader kicker="CERTIFICATIONS" title="Third-party validated. Field proven." testIdPrefix="certs" />
          <div className="mt-10 flex flex-wrap gap-4" data-testid="certifications-list">
            {["Korea Eco-Label", "ISO 9001 aligned", "Non-conductive up to 50kV", "20 Yrs Zero Incidents"].map((c) => (
              <div key={c} className="px-5 py-3 border border-amber-500/30 text-amber-300 font-mono-spec text-xs bg-slate-950">
                {c}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
